/**
 * OBD-II diagnostic trouble codes: the wire encoding, and human text in both
 * languages.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *  TWO DIFFERENT THINGS ARRIVE FROM A TELTONIKA DEVICE. DO NOT CONFLATE THEM.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *  1. **AVL id 281 — already text.** The device's own OBD stack has formatted the
 *     codes; the element is a variable-length US-ASCII string of mnemonics separated
 *     by commas ("P0301,P0420"). Three independent sources agree (the FMC003 wiki
 *     scrape types it ASCII, filipkroca types it String with no conversion, and
 *     Traccar's only transform is swapping the comma for a space). Nothing has to be
 *     bit-split here — `parseDtcPayload` splits on commas and that is the whole job.
 *
 *  2. **A raw two-byte SAE word** — what a mode-03 response carries, and what any
 *     integration that reaches the ECU directly will hand us. That one *is* packed,
 *     and `decodeDtcWord` implements the split.
 *
 *  Id 30 ("Number of DTC") is a third thing again: a one-byte COUNT. It is not
 *  derivable from either list and neither list is derivable from it — a device can
 *  report a count of 3 and a truncated string, since id 281 caps at 128 bytes.
 *
 *  AVL id 282 (fault codes over the LV-CAN200 adapter) is deliberately unmapped: its
 *  source type column is literally "-" and no parser implements it. Assuming it
 *  mirrors 281 would print invented mnemonics. See `AVL_DELIBERATELY_UNMAPPED`.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *  AN UNKNOWN CODE IS STILL A FAULT — AND IT IS NEVER FULLY UNKNOWN.
 * ─────────────────────────────────────────────────────────────────────────────
 * `describeDtc` never returns undefined and never throws. A code the dictionary does
 * not carry comes back with its printed mnemonic, null descriptions and
 * `descriptionKnown: false`, at severity `warning` — not `info`. A fault nobody can
 * name is not less serious than one we happen to have translated; swallowing it, or
 * grading it down, is how a manufacturer-specific engine fault disappears from a
 * fleet manager's screen.
 *
 * But "we have no sentence for this code" is not the same as "we know nothing about
 * it". SAE's numbering is systematic, and three facts are readable from the mnemonic
 * itself without any dictionary at all:
 *
 *   - the SYSTEM         — P / C / B / U, the first letter
 *   - the SUBSYSTEM      — `dtcFamily()`, from the numeric range
 *   - whether it is MANUFACTURER-SPECIFIC — `isManufacturerSpecific()`, from the
 *     second character
 *
 * So an unnamed `P1234` still renders as "Code constructeur — groupe motopropulseur",
 * which tells a fleet manager where to look and which documentation to open. That is
 * derived, not invented: the alternative — printing a guessed description because the
 * generic P1234 "usually" means something — sends a truck to the workshop for the
 * wrong part.
 */
import { DTC_ROWS } from './dtc-catalogue.js';
import { DTC_SEVERITY_RANK } from './dtc-severity.js';
export { DTC_SEVERITIES } from './dtc-severity.js';
/** First character of a mnemonic: the system the code belongs to. */
export const DTC_SYSTEMS = ['P', 'C', 'B', 'U'];
export const DTC_SYSTEM_LABELS = {
    P: { fr: 'Groupe motopropulseur', en: 'Powertrain' },
    C: { fr: 'Châssis', en: 'Chassis' },
    B: { fr: 'Carrosserie', en: 'Body' },
    U: { fr: 'Réseau / communication', en: 'Network / communication' },
};
const DTC_PATTERN = /^[PCBU][0-3][0-9A-F]{3}$/;
export const DTC_DICTIONARY = new Map(DTC_ROWS.map(([code, descriptionFr, descriptionEn, severity]) => [
    code,
    { descriptionFr, descriptionEn, severity },
]));
/**
 * The subsystem for a `P` code, by the character after the code-type digit.
 *
 * The hundreds group is SAE's own partitioning of the powertrain range: P03xx is
 * ignition and misfire wherever it appears, P07xx–P09xx is transmission, and so on.
 * `P0Axx`–`P0Dxx` are the hybrid and electric propulsion blocks — the third character
 * is hex, which is why this is keyed by character and not by a numeric range.
 */
const POWERTRAIN_GENERIC_FAMILIES = {
    '0': { fr: 'Dosage air / carburant', en: 'Fuel and air metering' },
    '1': { fr: 'Dosage air / carburant', en: 'Fuel and air metering' },
    '2': { fr: 'Injection et admission', en: 'Fuel injection and air intake' },
    '3': { fr: 'Allumage et ratés d’allumage', en: 'Ignition system and misfire' },
    '4': { fr: 'Dépollution et recyclage des gaz', en: 'Emission control and EGR' },
    '5': { fr: 'Vitesse, ralenti et entrées auxiliaires', en: 'Speed, idle control and auxiliary inputs' }, // prettier-ignore
    '6': { fr: 'Calculateur et circuits de sortie', en: 'Control module and output circuits' },
    '7': { fr: 'Transmission', en: 'Transmission' },
    '8': { fr: 'Transmission', en: 'Transmission' },
    '9': { fr: 'Transmission', en: 'Transmission' },
    A: { fr: 'Propulsion hybride ou électrique', en: 'Hybrid or electric propulsion' },
    B: { fr: 'Propulsion hybride ou électrique', en: 'Hybrid or electric propulsion' },
    C: { fr: 'Propulsion hybride ou électrique', en: 'Hybrid or electric propulsion' },
    D: { fr: 'Propulsion hybride ou électrique', en: 'Hybrid or electric propulsion' },
};
/** The P2xxx block, which SAE partitions differently from P0xxx. */
const POWERTRAIN_P2_FAMILIES = {
    '0': { fr: 'Dépollution, carburant et admission', en: 'Emissions, fuel and air intake' },
    '1': { fr: 'Papillon motorisé et injection', en: 'Throttle actuator and fuel injection' },
    '2': { fr: 'Capteurs moteur', en: 'Engine sensors' },
    '3': { fr: 'Allumage', en: 'Ignition system' },
    '4': { fr: 'Post-traitement des gaz (FAP, EGR)', en: 'Exhaust after-treatment (DPF, EGR)' },
    '5': { fr: 'Entrées et sorties auxiliaires', en: 'Auxiliary inputs and outputs' },
    '6': { fr: 'Calculateur et circuits de sortie', en: 'Control module and output circuits' },
    '7': { fr: 'Transmission', en: 'Transmission' },
    '8': { fr: 'Transmission', en: 'Transmission' },
    '9': { fr: 'Transmission', en: 'Transmission' },
    A: { fr: 'Post-traitement et dépollution', en: 'After-treatment and emissions' },
    B: { fr: 'Post-traitement et dépollution', en: 'After-treatment and emissions' },
};
/**
 * The network block. U01xx and U02xx are both "a module stopped answering", which is
 * the distinction that matters in a workshop: a bus fault and a single silent ECU are
 * found with different tools.
 */
const NETWORK_FAMILIES = {
    '0': { fr: 'Bus de communication', en: 'Communication bus' },
    '1': { fr: 'Perte de communication avec un calculateur', en: 'Lost communication with a control module' }, // prettier-ignore
    '2': { fr: 'Perte de communication avec un calculateur', en: 'Lost communication with a control module' }, // prettier-ignore
    '3': { fr: 'Logiciel et configuration des calculateurs', en: 'Control module software and configuration' }, // prettier-ignore
    '4': { fr: 'Données invalides reçues d’un calculateur', en: 'Invalid data received from a control module' }, // prettier-ignore
};
/**
 * Chassis and body are deliberately COARSE.
 *
 * SAE's generic C and B ranges are thin and most of what is in circulation is
 * manufacturer-defined, so a fine-grained split would be a split of codes we do not
 * actually have. One true sentence beats four confident ones.
 */
const CHASSIS_FAMILY = {
    fr: 'Freinage, suspension et direction',
    en: 'Braking, suspension and steering',
};
const BODY_FAMILY = {
    fr: 'Sécurité passive, habitacle et carrosserie',
    en: 'Passive safety, cabin and body',
};
/**
 * Whether SAE reserves this code's range for the vehicle manufacturer.
 *
 * The second character is the code type. `1` is manufacturer-specific in every system.
 * Beyond that the systems diverge, which is the whole reason this is a function and
 * not a regex:
 *
 *   P  — P0xxx and P2xxx are generic. P30xx–P33xx are manufacturer, P34xx and above
 *        are generic again.
 *   C, B, U — C2xxx / B2xxx / U2xxx are manufacturer as well.
 *
 * A malformed token returns false: we do not know that it is manufacturer-specific,
 * and claiming so would be the same invention this module refuses everywhere else.
 */
export function isManufacturerSpecific(code) {
    const normalized = normalizeDtcCode(code);
    if (!DTC_PATTERN.test(normalized))
        return false;
    const system = normalized[0];
    const type = normalized[1];
    if (type === '1')
        return true;
    if (system === 'P') {
        // P30xx–P33xx only. `'A' < '4'` is false, so P3Axx correctly stays generic.
        return type === '3' && (normalized[2] ?? '') < '4';
    }
    return type === '2';
}
/**
 * The subsystem a code's range belongs to, or null when the range says nothing.
 *
 * This works on manufacturer codes too — P1xxx follows the same hundreds partitioning
 * as P0xxx — which is what makes an untranslatable code still readable: "Code
 * constructeur — transmission" sends someone to the right chapter of the right manual.
 */
export function dtcFamily(code) {
    const normalized = normalizeDtcCode(code);
    if (!DTC_PATTERN.test(normalized))
        return null;
    const system = normalized[0];
    const type = normalized[1];
    const group = normalized[2] ?? '';
    if (system === 'P') {
        if (type === '2')
            return POWERTRAIN_P2_FAMILIES[group] ?? null;
        // P3xxx has no agreed subsystem partitioning outside the cylinder-deactivation
        // block, so it deliberately returns null rather than a plausible-sounding label.
        if (type === '3') {
            return group === '4'
                ? { fr: 'Désactivation de cylindres', en: 'Cylinder deactivation' }
                : null;
        }
        return POWERTRAIN_GENERIC_FAMILIES[group] ?? null;
    }
    if (system === 'U')
        return NETWORK_FAMILIES[group] ?? null;
    if (system === 'C')
        return CHASSIS_FAMILY;
    if (system === 'B')
        return BODY_FAMILY;
    return null;
}
export function dtcFamilyLabel(family, locale) {
    return locale === 'fr' ? family.fr : family.en;
}
/* ────────────────────────────────────────────────────────────────────────────
 * THE WIRE ENCODING
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * A two-byte SAE word → its mnemonic.
 *
 *   bits 15..14 → system letter, index into ['P','C','B','U']
 *   bits 13..0  → the four printed characters, as hex
 *
 * The second form is not an approximation of the first: a 14-bit value maxes at
 * 0x3FFF, so its leading hex digit is necessarily 0–3 — which IS bits 13..12, the
 * code-type digit. Worked example from python-OBD: 0x4123 → C0123.
 *
 * 0x0000 decodes faithfully to "P0000". In a mode-03 response that word is the
 * padding SAE uses for "no more codes", which is why `decodeDtcWords` drops it —
 * but the decoder itself does not silently swallow a value it was handed.
 */
export function decodeDtcWord(word) {
    if (!Number.isInteger(word) || word < 0 || word > 0xffff)
        return null;
    const system = DTC_SYSTEMS[(word >> 14) & 0b11];
    const digits = (word & 0x3fff).toString(16).toUpperCase().padStart(4, '0');
    return `${system}${digits}`;
}
/** Inverse of `decodeDtcWord`. Null for anything that is not a well-formed mnemonic. */
export function encodeDtcCode(code) {
    const normalized = normalizeDtcCode(code);
    if (!DTC_PATTERN.test(normalized))
        return null;
    const system = DTC_SYSTEMS.indexOf(normalized[0]);
    const digits = Number.parseInt(normalized.slice(1), 16);
    if (system < 0 || Number.isNaN(digits))
        return null;
    return (system << 14) | digits;
}
/**
 * A list of two-byte words → mnemonics, with the 0x0000 padding dropped.
 * Malformed entries are dropped too — `decodeDtcWord` already refuses them.
 */
export function decodeDtcWords(words) {
    const out = [];
    for (const word of words) {
        if (word === 0)
            continue;
        const code = decodeDtcWord(word);
        if (code !== null)
            out.push(code);
    }
    return out;
}
export function normalizeDtcCode(code) {
    return code.trim().toUpperCase();
}
export function isDtcCode(code) {
    return DTC_PATTERN.test(normalizeDtcCode(code));
}
/**
 * AVL id 281's payload → the mnemonics it lists.
 *
 * The bytes are already text; the only work is splitting on the comma the device
 * uses, trimming and dropping empties. An empty payload means "no active DTC" and
 * yields an empty list — which is a statement, not an absence.
 *
 * Tokens that do not look like SAE mnemonics are KEPT, not filtered: the device said
 * something, and dropping it would leave a fault visible on the dashboard of the
 * vehicle and invisible in the fleet platform.
 */
export function parseDtcPayload(payload) {
    if (payload === null || payload === undefined)
        return [];
    return payload
        .split(/[,;\s]+/)
        .map((token) => normalizeDtcCode(token))
        .filter((token) => token.length > 0);
}
/* ────────────────────────────────────────────────────────────────────────────
 * DESCRIPTION
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * Describes a code. Never throws, never returns undefined, never swallows.
 *
 * An unknown code comes back with `descriptionKnown: false` and null descriptions so
 * the UI prints the raw mnemonic and says the description is unknown, in the user's
 * language, from its own catalogue. Returning a placeholder string here would put
 * untranslated English in a French report.
 *
 * The family and the manufacturer flag are filled in either way. They are read off
 * the mnemonic's own structure, so they are as true for a code we cannot name as for
 * one we can — and they are the difference between a blank row and a row that says
 * where to look.
 */
export function describeDtc(code) {
    const normalized = normalizeDtcCode(code);
    const malformed = !DTC_PATTERN.test(normalized);
    // Null rather than a defaulted 'P': claiming a garbage token is a powertrain fault
    // is the same invention this module exists to refuse.
    const system = DTC_SYSTEMS.includes(normalized[0] ?? '')
        ? normalized[0]
        : null;
    const family = dtcFamily(normalized);
    const manufacturerSpecific = isManufacturerSpecific(normalized);
    const definition = DTC_DICTIONARY.get(normalized);
    if (definition === undefined) {
        return {
            code: normalized,
            system,
            descriptionFr: null,
            descriptionEn: null,
            // Not `info`. A fault we cannot name is not a fault that matters less.
            severity: 'warning',
            descriptionKnown: false,
            familyFr: family?.fr ?? null,
            familyEn: family?.en ?? null,
            manufacturerSpecific,
            malformed,
        };
    }
    return {
        code: normalized,
        system,
        descriptionFr: definition.descriptionFr,
        descriptionEn: definition.descriptionEn,
        severity: definition.severity,
        descriptionKnown: true,
        familyFr: family?.fr ?? null,
        familyEn: family?.en ?? null,
        manufacturerSpecific,
        malformed,
    };
}
/** Describes a raw two-byte word. Null only when the word is not a 16-bit integer. */
export function describeDtcWord(word) {
    const code = decodeDtcWord(word);
    return code === null ? null : describeDtc(code);
}
/** Describes every code in an id-281 payload, in wire order, duplicates preserved. */
export function describeDtcPayload(payload) {
    return parseDtcPayload(payload).map((code) => describeDtc(code));
}
export function dtcDescription(described, locale) {
    return locale === 'fr' ? described.descriptionFr : described.descriptionEn;
}
/** The subsystem, for the locale. Null when the range carries no agreed meaning. */
export function dtcFamilyOf(described, locale) {
    return locale === 'fr' ? described.familyFr : described.familyEn;
}
export function dtcSystemLabel(system, locale) {
    const label = DTC_SYSTEM_LABELS[system];
    return locale === 'fr' ? label.fr : label.en;
}
/** The worst severity in a set, or null for an empty set — never a default of `info`. */
export function worstDtcSeverity(codes) {
    let worst = null;
    for (const described of codes) {
        if (worst === null || DTC_SEVERITY_RANK[described.severity] > DTC_SEVERITY_RANK[worst]) {
            worst = described.severity;
        }
    }
    return worst;
}
//# sourceMappingURL=dtc.js.map